import getDashboardName from 'common/src/modules/analytics/utils/getDashboardName';
import dashboardsApi from 'common/src/modules/dashboards/dashboards.api';
import {updateDashboards} from 'common/src/modules/dashboards/store/dashboardPage.slice';
import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import _find from 'lodash/find';
import _map from 'lodash/map';
import {z} from 'zod';
import {AccountAnalyticsToolName} from '../accountAnalyticsAgent.enums';

const inputSchema = z.object({
  dashboardId: z
    .number()
    .describe('The ID of the editable dashboard to rename')
    .nullable()
    .optional(),
  dashboardName: z
    .string()
    .describe('The name of the editable dashboard to rename')
    .nullable()
    .optional(),
  name: z.string().describe('The new name of the dashboard'),
});

function renameAccountDashboardTool(store: CLStore) {
  async function execute({
    input: {dashboardId, dashboardName, name},
  }: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const state = store.getState();
    const {
      dashboardPage: {dashboards, dashboardId: currentDashboardId},
    } = state;

    const accountDashboards = _map(dashboards, (dashboard) => ({
      ...dashboard,
      name: getDashboardName(dashboard),
    }));

    const dashboardToRename = _find(
      accountDashboards,
      ({id, name}) => id === dashboardId || name === dashboardName || id === currentDashboardId,
    );

    if (!dashboardToRename) {
      return toolError('Dashboard not found');
    }

    await dashboardsApi.updateDashboard({
      ...dashboardToRename,
      name,
    });

    const updatedDashboards = _map(dashboards, (dashboard) => ({
      ...dashboard,
      name: dashboard.id === dashboardToRename.id ? name : dashboard.name,
    }));

    store.dispatch(updateDashboards(updatedDashboards));

    return toolResult('Dashboard renamed successfully');
  }

  return tool({
    name: AccountAnalyticsToolName.RenameAccountDashboard,
    displayName: 'Renaming dashboard',
    description: 'rename a dashboard',
    execute,
    inputSchema,
  });
}

export default renameAccountDashboardTool;
